/* eslint-disable no-restricted-imports */
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useNotifications } from '@/data/Content/Notifications';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { processError } from '@/data/utils/processError';
import { Switch } from '@/utils/switch';
import { useIsLoadingBarNextRoute } from 'integration/hooks/useIsLoadingNextRoute';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { parseUserPreferredSizes } from 'integration/hooks/usePreferredSizes';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import {
	initialDpFanStages,
	dpFanStagesReducer,
	DpFanStagesStateSports,
	DpFanStagesStateTeams,
	DpFanStagesStateSizes,
	DP_FAN_STAGES,
	DP_FAN_STAGES_ACTIONS,
} from '@/data/Content/DpFanStages/state';
import {
	DpFanAnyItem,
	DpFanDayByDay,
	DpFanSports,
	DpFanTeams,
} from '@/data/Content/DpFanStages/types';
import {
	getLocalStorageDpFanState,
	removeLocalStorageDpFanState,
	setLocalStorageDpFanState,
} from '@/data/Content/DpFanStages/localStorage';
import { generateDpFanUpdateBody } from '@/data/Content/DpFanStages/utils';
import { useDpFanGroupsFetcher } from '@/data/Content/DpFanStages/fetch';
import { formatDateToDisplay } from '@/data/utils/convertDateToBackendFormat';
import { useUpdatePreferredSizes } from '@/data/Content/DpFanStages/useUpdatePreferredSizes';

export const useDpFanStages = () => {
	const router = useNextRouter();
	const [dpFanState, dispatch] = useReducer(dpFanStagesReducer, initialDpFanStages);
	const openAuthModal = useIsOpen();
	const { notifyError } = useNotifications();
	const { personInfo, isLoadingPersonInfo, onUpdatePreferredSizes } = useUpdatePreferredSizes();

	const {
		stylesListData,
		brandsListData,
		dayByDayListData,
		userBrandsData,
		userStylesData,
		userDayByDayData,
		preferredSizes,
		interests,
		isUserLoggedIn,
		isLoadingPreferredSizes,
		isLoadingInterests,
		isLoadingUserInterests,
		onUpdateUserInterests,
	} = useDpFanGroupsFetcher();

	const { visible: isLoadingNextRoute } = useIsLoadingBarNextRoute({
		startDelayMs: 50,
		finishHoldMs: 500,
	});

	const onSyncDataUserInterests = useCallback(() => {
		const prevUserSports = userStylesData?.children.map((item) => item.name) || [];
		const prevUserTeams = userBrandsData?.children.map((item) => item.name) || [];
		const prevUserDayByDay = userDayByDayData?.children.map((item) => item.name) || [];
		const userPreferredSizes = parseUserPreferredSizes(personInfo?.x_userProfileField1);

		let prevUserSizes = userPreferredSizes.reduce((acc, size) => {
			acc[size.label] = size.value;
			return acc;
		}, {} as DpFanStagesStateSizes);

		if (personInfo.gender) {
			prevUserSizes = {
				...prevUserSizes,
				GENDER: Switch(personInfo.gender)
					.case('Male', () => 'Masculino')
					.case('Female', () => 'Femenino')
					.defaultTo(() => 'Prefiero no decirlo'),
			};
		}

		if (personInfo.dateOfBirth) {
			prevUserSizes = {
				...prevUserSizes,
				BIRTHDAY: formatDateToDisplay(personInfo.dateOfBirth),
			};
		}

		return {
			sports: prevUserSports,
			teams: prevUserTeams,
			dayByDay: prevUserDayByDay,
			size: prevUserSizes,
		};
	}, [
		userStylesData,
		userBrandsData,
		userDayByDayData,
		personInfo?.x_userProfileField1,
		personInfo?.gender,
		personInfo?.dateOfBirth,
	]);

	const onGetCurrentState = useCallback(() => {
		if (dpFanState.stage !== DP_FAN_STAGES.INITITAL) {
			return { ...dpFanState, needToSync: false };
		}

		const savedState = getLocalStorageDpFanState();

		if (savedState) {
			removeLocalStorageDpFanState();
			return {
				...JSON.parse(savedState),
				needToSync: false,
			};
		}

		return {
			...dpFanState,
			...onSyncDataUserInterests(),
			needToSync: false,
		};
	}, [dpFanState, onSyncDataUserInterests]);

	useEffect(() => {
		if (
			!isLoadingUserInterests &&
			!isLoadingInterests &&
			!isLoadingNextRoute &&
			!isLoadingPreferredSizes &&
			!isLoadingPersonInfo &&
			dpFanState.stage === DP_FAN_STAGES.INITITAL &&
			dpFanState.needToSync
		) {
			const newState = onGetCurrentState();
			dispatch({
				type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE,
				updates: newState,
			});
		}
	}, [
		isLoadingPersonInfo,
		isLoadingUserInterests,
		isLoadingNextRoute,
		isLoadingPreferredSizes,
		isLoadingInterests,
		dpFanState.needToSync,
		dpFanState.stage,
		onGetCurrentState,
	]);

	const sports = useMemo<DpFanSports>(
		() =>
			stylesListData?.children.map((item) => ({
				id: item.id,
				name: item.name,
				parentId: item.parentId,
				hasImage: item.hasImage,
			})) || [],
		[stylesListData]
	);

	const teams = useMemo<DpFanTeams>(
		() =>
			brandsListData?.children.map((item) => ({
				id: item.id,
				name: item.name,
				parentId: item.parentId,
				hasImage: item.hasImage,
			})) || [],
		[brandsListData]
	);

	const dayByDay = useMemo<DpFanDayByDay>(
		() =>
			dayByDayListData?.children.map((item) => ({
				id: item.id,
				name: item.name,
				parentId: item.parentId,
				hasImage: item.hasImage,
			})) || [],
		[dayByDayListData]
	);

	const onChangeSport = useCallback((sport: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_SPORT, sport });
	}, []);

	const onChangeTeam = useCallback((team: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_TEAM, team });
	}, []);

	const onChangeDayByDay = useCallback((dayByDay: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_DAY_BY_DAY, dayByDay });
	}, []);

	const onChangeSize = useCallback((category: string, value: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.CHANGE_SIZE, category, size: value });
	}, []);

	const onPrevStage = useCallback(() => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.PREV_STAGE });
	}, []);

	const onFinish = useCallback(async () => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.START_SUBMIT });
		openAuthModal.off();

		const syncDpFanState = onGetCurrentState();

		try {
			const newUserInterests = generateDpFanUpdateBody({
				selectedSports: syncDpFanState.sports,
				selectedTeams: syncDpFanState.teams,
				selectedDayByDay: syncDpFanState.dayByDay,
				sports: stylesListData,
				teams: brandsListData,
				dayByDay: dayByDayListData,
			});

			await onUpdateUserInterests(newUserInterests);

			await onUpdatePreferredSizes(syncDpFanState.size);

			await router.replace('/estilo-finalizado');
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));

			dispatch({
				type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE,
				updates: { ...syncDpFanState, submitting: false, needToSync: false },
			});
		}
	}, [
		stylesListData,
		brandsListData,
		dayByDayListData,
		onGetCurrentState,
		onUpdateUserInterests,
		notifyError,
		openAuthModal,
		router,
		onUpdatePreferredSizes,
	]);

	const onNextStage = useCallback(async () => {
		if (dpFanState.stage === DP_FAN_STAGES.SIZES) {
			if (!isUserLoggedIn) {
				setLocalStorageDpFanState({ ...dpFanState, submitting: true });
				openAuthModal.on();
				dispatch({ type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE, updates: { submitting: true } });
				return;
			}

			await onFinish();
			return;
		}

		dispatch({ type: DP_FAN_STAGES_ACTIONS.NEXT_STAGE });
	}, [dpFanState, isUserLoggedIn, openAuthModal, onFinish]);

	const onUpdateUserSizes = useCallback(async () => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.START_SUBMIT });

		try {
			await onUpdatePreferredSizes(dpFanState.size);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			dispatch({
				type: DP_FAN_STAGES_ACTIONS.END_SUBMIT,
			});
		}
	}, [dpFanState.size, notifyError, onUpdatePreferredSizes]);

	const onChangeActiveSport = useCallback((sport: string) => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE, updates: { activeCategory: sport } });
	}, []);

	const onRemoveActiveSport = useCallback(() => {
		dispatch({
			type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE,
			updates: { ...onSyncDataUserInterests(), activeCategory: undefined },
		});
	}, [onSyncDataUserInterests]);

	const onUpdateUserTeams = useCallback(async () => {
		dispatch({ type: DP_FAN_STAGES_ACTIONS.START_SUBMIT });

		try {
			const dpFanUpdateBody = generateDpFanUpdateBody({
				selectedTeams: dpFanState.teams,
				teams: brandsListData,
			});

			await onUpdateUserInterests(dpFanUpdateBody);
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
		} finally {
			dispatch({
				type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE,
				updates: { submitting: false, activeCategory: undefined },
			});
		}
	}, [dpFanState.teams, onUpdateUserInterests, brandsListData, notifyError]);

	const onUpsertUserSport = useCallback(
		async (sportId: number) => {
			const sport = sports.find((sport) => sport.id === sportId);

			if (!sport) return;

			const isExistSport = dpFanState.sports.find((_sport) => _sport === sport.name);

			const newSports = isExistSport
				? dpFanState.sports.filter((_sport) => _sport !== sport.name)
				: [...dpFanState.sports, sport.name];

			const dpFanUpdateBody = generateDpFanUpdateBody({
				selectedSports: newSports,
				sports: stylesListData,
			});

			dispatch({ type: DP_FAN_STAGES_ACTIONS.START_SUBMIT });

			try {
				await onUpdateUserInterests(dpFanUpdateBody);

				onChangeSport(sport.name);
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			} finally {
				dispatch({
					type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE,
					updates: { submitting: false },
				});
			}
		},
		[sports, onUpdateUserInterests, onChangeSport, notifyError, dpFanState.sports, stylesListData]
	);

	const onCloseAuthModal = useCallback(() => {
		removeLocalStorageDpFanState();
		openAuthModal.off();
		dispatch({ type: DP_FAN_STAGES_ACTIONS.PARTIAL_UPDATE, updates: { submitting: false } });
	}, [openAuthModal]);

	const skipBirthday = !!personInfo?.dateOfBirth;

	return {
		dpFanState,
		isOpenAuthModal: openAuthModal.open,
		preferredSizes,
		interests,
		sports,
		teams,
		dayByDay,
		personInfo,
		skipBirthday,
		isLoadingUserInterests,
		isLoadingInterests,
		isLoadingNextRoute,
		isLoadingPreferredSizes,
		isLoadingPersonInfo,
		onChangeSize,
		onCloseAuthModal,
		onChangeSport,
		onChangeTeam,
		onChangeDayByDay,
		onPrevStage,
		onNextStage,
		onFinish,
		onUpdateUserSizes,
		onChangeActiveSport,
		onRemoveActiveSport,
		onUpdateUserTeams,
		onUpsertUserSport,
		onSyncDataUserInterests,
	};
};

export {
	type DpFanStagesStateSports,
	type DpFanStagesStateTeams,
	type DpFanStagesStateSizes,
	DP_FAN_STAGES,
	DP_FAN_STAGES_ACTIONS,
};

export type { DpFanAnyItem, DpFanSports, DpFanTeams };
